import { orderedVehicleCategories } from "../shared/pricing/constants";
import { useEffect, useState, useMemo, useRef } from "react";
import Fuse from "fuse.js";
import { Icon } from "../components/Icon";
import { useNavigate, useLocation } from "react-router-dom";
import { apiRequest } from "../utils/api";
import { startAppCall } from "../utils/appCall";
import { cachedList, isFresh, fetchList, prefetchList, carsListPath, countNamed } from "../utils/listCache";
import { intentDrop, parseQuery } from "../utils/queryParse";
import { track } from "../utils/analytics";
import { callWithWalletGate, WALLET_GATE_CANCELLED } from "../utils/walletGate";
import { readTripExtras } from "../utils/tripExtras";

import { useAuth } from "../context/AuthContext";
import { getAuth } from "../utils/auth";
import { detectCityFromGPS, canonicalCity, cityWordsInEnglish } from "../utils/cities";
import { loadHireTripContext, saveHireTripContext, locationText, hasHireTripContext, hasStoredHireTripContext } from "../utils/hireTripContext";
import { tripQuery, readTripQuery, syncTripUrl, shareTripLink } from "../utils/tripLink";
import { TripsRow, TripsSheet, TripsCard, useSeasonTripsConfig } from "../components/home/SeasonTrips";
import { DesktopHero, DesktopSearchBar, FilterPanel, DesktopFooter, TrustRow, toggled } from "../components/home/DesktopAtoms";
import { localCity } from "../utils/localNames";
import { SEASON_CITY, SEASON_FROM, TRIP_TEXT, seasonTrip, tripText } from "../data/seasonTrips";
import { TripIcons } from "../components/trip/TripFormAtoms";
import { computeHireFare, formatHireFare } from "../shared/pricing/hireFare";
import { calculateTripMetrics } from "../utils/distanceUtils";
import BottomNav from "../components/BottomNav";
import { usePageMeta } from "../hooks/usePageMeta";
import HireRatingModal from "../components/HireRatingModal";
import EmptyStateBanner from "../components/EmptyStateBanner";
import Header from "../components/Header";
import SearchBar from "../components/SearchBar";
import { logSearch, cancelSearchLog } from "../utils/searchLog";
import FilterSheet from "../components/home/FilterSheet";
import {
  GuestBanner, WhereCard, QuickChip, ChipRow, ResultStrip, ListMessage, Loading, RecentEnquiryCard,
  useMyRecentLeads, recentEnquiryOf, hideEnquiry, tripWhen, shortPlace, countWords, openCityPicker, PRIMARY_BTN, SECONDARY_BTN,
} from "../components/home/HomeAtoms";
import { useLanguage } from "../context/LanguageContext";
import { fillText, dateTimeText } from "../utils/leadDisplay";
import { useLocalNames } from "../utils/localNames";
import { driveTypeWords } from "../components/detail/DetailAtoms";
import { askOnBehalf, onBehalfBody, ON_BEHALF_CANCELLED } from "../components/OnBehalfHost";
import { CallToBookBar, CallToBookLine, WantUsToBook } from "../components/home/CallToBook";
import { showBusinessLimit } from "../components/BusinessLimitHost";
import { loadGoogleMaps } from "../utils/googleMaps";
import {
  DRIVER_LANGS, DRIVER_TRIPS, EXPERIENCE, RATINGS, optionLabel, splitValues,
  nativeLanguageNames, driverTripMatches, expFloor, isAvailableNow, DRIVER_SORTS, DRIVER_BUDGETS, inDriverBudget,
} from "../utils/listChoices";

function cleanLocation(loc) {
  if (!loc) return "";
  return loc.replace(/\bnear\b\s*/gi, "").replace(/\barea\b/gi, "").trim();
}

/* The categories a driver can be filtered by — read, not copied.
   This list was frozen at fifteen: it never gained the four bus tiers, let alone
   anything an admin has added since, so a driver on one of those could not be found by
   the filter that was supposed to find them. Called rather than captured at module
   load, because the admin config arrives after the bundle does. */
const vehicleTypes = () => orderedVehicleCategories();

/* The filters on this list, all stored as comma lists like the Cars list's:
   language ("Telugu,Hindi"), experience ("5+ Years"), rating ("4+ Stars"), tripType
   ("Local,Outstation") and availability ("now"). */
const NO_FILTERS = { language: "", experience: "", rating: "", tripType: "", availability: "" };

/* Which drivers a set of filters keeps. One function, used by the list and by the
   Filters sheet's "Show 12 drivers", so the number on the button is the number shown. */
function applyDriverFilters(list, f) {
  let result = list;

  // The lowest band chosen: "3+" and "10+" together keep everyone from 3 years up.
  const expMins = splitValues(f.experience).map((v) => parseInt(v, 10)).filter(Number.isFinite);
  if (expMins.length) {
    const min = Math.min(...expMins);
    result = result.filter((d) => {
      const years = expFloor(d.drivingExperience ?? d.experienceYears);
      return years != null && years >= min;
    });
  }

  const langs = splitValues(f.language);
  if (langs.length) {
    result = result.filter((d) => {
      const spoken = Array.isArray(d.languagesSpoken)
        ? d.languagesSpoken
        : String(d.languagesSpoken || "").split(",").map((s) => s.trim());
      return spoken.some((l) => langs.some((fl) => String(l).toLowerCase().includes(fl.toLowerCase())));
    });
  }

  const trips = splitValues(f.tripType);
  if (trips.length) result = result.filter((d) => driverTripMatches(trips, d.availability));

  const ratingMins = splitValues(f.rating).map((v) => parseInt(v, 10)).filter(Number.isFinite);
  if (ratingMins.length) {
    const min = Math.min(...ratingMins);
    result = result.filter((d) => (d.averageRating ?? 0) >= min);
  }

  if (f.availability) result = result.filter((d) => isAvailableNow(d.availabilityStatus));

  return result;
}

/* ── Driver Card ───────────────────────────────────────────────────────
   One driver a row (the owner's layout, Oct 2026): the photo on the left with "New on
   Sramika" and the driver's status on it, who they are in the middle, the fare and Call
   at the bottom right, and the car types they can drive along the bottom. Every word
   in the reader's language. */
function DriverCard({ d, onHire, onCallToBook, callingId, onViewProfile, onSeePrice, matchReason, hasTripContext, fare }) {
  const { t, lang } = useLanguage();
  // The driver's name in the reader's script - display only.
  const ln = useLocalNames();
  const nameL = ln(d.driverName || "", "person");
  const photoUrl = d.profilePhotoUrl || d.selfiePhotoUrl;
  const initials = nameL !== (d.driverName || "") ? nameL.slice(0, 1) : (d.driverName || "?").slice(0, 2).toUpperCase();
  const [imgFailed, setImgFailed] = useState(false);
  const showImg = photoUrl && !imgFailed;

  // Each language named in the reader's language ("తెలుగు · ఇంగ్లీష్").
  const langs = nativeLanguageNames(d.languagesSpoken, lang);

  /* "10-15 years" is the stored form; the numbers are kept and "yrs driving" said in
     the reader's language. */
  const experienceStr = (() => {
    const raw = String(d.drivingExperience || d.experienceYears || "").trim();
    if (!raw) return "";
    return raw.replace(/\s*years?\s*$/i, "").replace(/\s+/g, " ").trim();
  })();

  // Ratings - the real aggregate, never a placeholder; a driver without one is new.
  const rating = Number(d.averageRating || 0);
  const ratingCount = Number(d.ratingCount || 0);
  const hasRating = rating > 0 && ratingCount > 0;

  /* The driver's own status, on the photo: Online, Busy or Offline. A record without
     one says nothing rather than guessing. */
  const av = String(d.availabilityStatus || "").trim().toLowerCase();
  const status = (av === "busy")
    ? { text: t("hm_busy"), cls: "bg-[#DC2626]/90" }
    : (av === "unavailable" || av === "offline")
      ? { text: t("hm_offline"), cls: "bg-amber-500/90" }
      : av
        ? { text: t("hm_online"), cls: "bg-[#2E7D32]/90" }
        : null;

  const isVerified = d.licenseStatus === "VALID" || d.status === "APPROVED";
  const nearKm = Number(d.distanceFromPickupKm);

  // The car types, in the words the driver's own page uses ("Sedan, SUV, Tempo Traveller").
  const types = driveTypeWords(d.vehicleTypes, t);

  /* What the price covers. Two drivers at different totals look arbitrary without it,
     and the basis was only ever stated inside the hire form. */
  const hrs = fare && !fare.blocked && Number.isFinite(Number(fare.hours)) ? Math.max(1, Math.round(fare.hours)) : 0;
  const fareBasis = hrs
    ? `${t(fare.category === "OUTSTATION" ? "hm_ttOut" : "hm_ttLocal")} · ${hrs === 1 ? t("hm_oneHr") : fillText(t("hs_hrs"), { n: hrs })}`
    : "";

  const calling = callingId === d.driverId;
  const firstName = String(d.driverName || "").trim().split(/\s+/)[0] || "";

  return (
    <div
      onClick={() => onViewProfile(d)}
      role="button"
      tabIndex={0}
      data-driver-card=""
      aria-label={fillText(t("hm_viewCar"), { name: nameL })}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onViewProfile(d); }}
      className="bg-white rounded-[16px] overflow-hidden p-[8px] cursor-pointer transition-all duration-200 ease-out border border-[#E2E8F0] shadow-[0_2px_12px_rgba(15,23,42,0.06)] active:scale-[0.99] md:hover:-translate-y-[1px] md:hover:shadow-[0_6px_24px_rgba(0,0,0,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7BAE00] focus-visible:ring-offset-2"
    >
      <div className="flex items-start gap-[9px]">
        {/* The driver: a narrow frame exactly as tall as the details and buttons beside
            it, so the photo never leaves empty space under them (the owner, Oct 2026). The
            crop is biased above centre rather than dead centre: these are phone selfies
            with head-room above the subject, and centring them framed the wall. */}
        <div className="relative shrink-0 self-stretch w-[18%] min-w-[61px] max-w-[77px] min-h-[58px] rounded-[10px] overflow-hidden bg-[#F6F8FC]">
          {showImg ? (
            <img
              loading="lazy" decoding="async" src={photoUrl} alt=""
              onError={() => setImgFailed(true)}
              className="absolute inset-0 w-full h-full object-cover object-[center_30%]"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#7BAE00] to-[#5E8700]">
              <span className="text-white text-[18px] font-bold">{initials}</span>
            </div>
          )}

          {!hasRating && (
            <span className="absolute z-20 top-[3px] left-[3px] max-w-[calc(100%-6px)] truncate rounded-[6px] bg-white px-[4px] py-[1px] text-[8px] font-bold text-[#5E8700] leading-tight shadow-[0_1px_3px_rgba(15,23,42,0.22)]">
              {/* "New" - the photo is too narrow for "New on Sramika". */}
              {t("hm_newTag")}
            </span>
          )}

          {status && (
            <span className={`absolute z-20 bottom-[3px] left-[3px] max-w-[calc(100%-6px)] inline-flex items-center gap-[2px] rounded-full px-[3px] py-[1px] text-[7px] font-bold text-white shadow-sm ${status.cls}`}>
              <span className="shrink-0 w-[4px] h-[4px] rounded-full bg-white" />
              <span className="min-w-0 truncate">{status.text}</span>
            </span>
          )}
        </div>

        {/* Who they are, and the facts a customer weighs. */}
        <div className="flex-1 min-w-0 py-[1px]">
          {/* Up to two lines. */}
          <h3 className="text-[13px] font-bold text-[#1F2937] leading-tight flex items-start gap-[5px]">
            <span className="min-w-0 line-clamp-2 break-words">{nameL || t("hm_grpDriver")}</span>
            {/* Verified, on the same APPROVED status the list is filtered on, so it is
                a fact about the driver rather than decoration. */}
            {isVerified && (
              <span className="shrink-0 w-[15px] h-[15px] mt-[1px] rounded-full bg-[#7BAE00] flex items-center justify-center">
                <Icon name="check" size={8} stroke="#FFFFFF" strokeWidth={4} />
              </span>
            )}
          </h3>

          {/* "Sramika Recommended" (mock Rec_1). */}
          {Date.parse(d.recommendedUntil || 0) > Date.now() && (
            <p className="mt-[3px]"><span className="inline-flex items-center gap-[3px] rounded-full bg-[#EAF5D3] px-1.5 py-[1px] text-[9px] font-extrabold text-[#3F5E00]">✓ {t("rc_badge")}</span></p>
          )}

          {hasRating && (
            <p className="mt-[3px] text-[9.5px] font-bold text-[#1F2937] leading-tight flex items-center gap-[3px]">
              <Icon name="star" size={10} fill="#F59E0B" stroke="#F59E0B" className="shrink-0" />
              {rating.toFixed(1)}
              <span className="font-medium text-[#64748B]">({fillText(t("hm_nReviews"), { n: ratingCount })})</span>
            </p>
          )}

          {experienceStr && (
            <p className="mt-[3px] text-[9.5px] font-medium text-[#475569] leading-tight flex items-center gap-[3px]">
              <Icon name="award" size={10} stroke="#7BAE00" strokeWidth={2} className="shrink-0" />
              <span><b className="font-bold text-[#5E8700]">{fillText(t("hm_yrs"), { yrs: experienceStr })}</b> {t("hm_driving")}</span>
            </p>
          )}

          <div className="flex items-center gap-x-[6px] gap-y-[3px] flex-wrap mt-[3px] text-[9.5px] font-semibold text-[#334155] leading-tight">
            {cleanLocation(d.locality || d.city || "") && (
              <span className="inline-flex items-center gap-[3px] min-w-0">
                <Icon name="mapPin" size={10} stroke="#7BAE00" strokeWidth={2} className="shrink-0" />
                <span className="min-w-0 truncate">{ln(cleanLocation(d.locality || d.city || ""), "place")}</span>
              </span>
            )}
            {/* How far away they are, from the same measurement the radius listing
                uses, so the number and the list cannot disagree. Green, because it is
                the one fact here that changes with where the customer is standing. */}
            {Number.isFinite(nearKm) && nearKm > 0 && (
              <span className="inline-flex items-center gap-[3px] whitespace-nowrap font-bold text-[#5E8700]">
                <Icon name="navigation" size={10} stroke="#5E8700" strokeWidth={2} />{fillText(t("hm_kmAway"), { km: nearKm })}
              </span>
            )}
          </div>

          {langs && (
            <p className="flex items-start gap-[3px] text-[9px] font-medium text-[#64748B] leading-snug mt-[3px]">
              <Icon name="globe" size={10} stroke="#7BAE00" strokeWidth={2} className="shrink-0 mt-[2px]" />
              <span className="min-w-0 line-clamp-2">{langs}</span>
            </p>
          )}

          {matchReason && (
            <p className="text-[9px] font-bold text-[#5E8700] leading-tight mt-[3px] truncate">
              {fillText(t("hm_matchedBy"), { what: matchReason })}
            </p>
          )}
        </div>

        {/* At the bottom right: the fare - or the way to it - with Call under it, the
            last thing the eye reaches (the owner's layout, Oct 2026). */}
        <div className="shrink-0 self-end w-[88px] flex flex-col items-stretch gap-[6px]">
          {fare?.blocked ? (
            // Beyond the city's single-driver distance limit. No driver can take it,
            // so a price would be misleading.
            <p className="text-[9.5px] font-bold text-[#DC3545] leading-tight text-center">{t("hm_tooLong")}</p>
          ) : fare ? (
            /* The price, the loudest thing on the card, because it is what the customer
               is deciding on - and what it covers under it. */
            <div className="text-center">
              <p className="text-[16.5px] font-extrabold text-[#0F172A] tracking-tight leading-none whitespace-nowrap">{formatHireFare(fare.total)}</p>
              {fareBasis && <p className="mt-[3px] text-[9px] font-medium text-[#64748B] leading-tight">{fareBasis}</p>}
            </div>
          ) : hasTripContext ? (
            // Route known but this driver has no usable rate — an older record that
            // predates rates being defaulted at registration.
            <p className="text-[9.5px] font-medium text-[#64748B] italic leading-tight text-center">{t("hm_priceOnReq")}</p>
          ) : (
            /* No trip yet: "See price ›", the words and look of the Cars cards (22 Oct 2026) -
               it opens the trip form, which brings the customer back to this driver. */
            <button type="button"
              onClick={(e) => { e.stopPropagation(); onSeePrice(d); }}
              className="w-full min-h-[44px] inline-flex items-center justify-center gap-[2px] text-[11px] font-extrabold text-[#5E8700] leading-tight active:opacity-70">
              <span className="min-w-0">{t("hm_seePrice")}</span>
              <Icon name="chevronRight" size={14} strokeWidth={2.6} className="shrink-0" />
            </button>
          )}

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onCallToBook ? onCallToBook(d) : onHire(d); }}
            disabled={calling}
            aria-label={firstName ? fillText(t("hs_callName"), { name: ln(firstName, "person") }) : t("hm_call")}
            className="w-full min-h-[44px] inline-flex items-center justify-center gap-[6px] rounded-[11px] bg-[#7BAE00] px-2 text-[11.5px] font-bold text-white shadow-[0_2px_6px_rgba(123,174,0,0.35)] active:scale-[0.98] transition-transform disabled:opacity-60 md:hover:bg-[#6E9D00]"
          >
            <Icon name="phone" size={14} stroke="#FFFFFF" fill="#FFFFFF" strokeWidth={1.4} />
            {calling ? "…" : t("hm_call")}
          </button>
        </div>
      </div>

      {/* Along the bottom: the car types they can drive, on one line - as many whole
          words as fit, the rest left off rather than cut ("Sedan, MPV, Tempo Traveller"),
          as the owner asked (Oct 2026). Each type is its own item; one that does not fit
          wraps to a second line that the one-line height hides - a line further down
          than usual, so the vowel signs of a hidden Telugu word do not peek in above. */}
      {types.length > 0 && (
        <div className="mt-[8px] pt-[6px] border-t border-[#EEF2F7] flex items-center gap-[4px] text-[9.5px] font-medium text-[#475569] leading-snug">
          <Icon name="car" size={11} stroke="#7BAE00" strokeWidth={2} className="shrink-0" />
          <p className="min-w-0 flex-1 flex flex-wrap gap-y-[1em] h-[1.6em] overflow-hidden">
            <span className="whitespace-nowrap mr-[3px] text-[#64748B]">{t("dp_canDrive")}:</span>
            {types.map((w, i) => (
              <b key={w} className="whitespace-nowrap font-semibold text-[#334155]">{i ? ", " : ""}{w}</b>
            ))}
          </p>
        </div>
      )}
    </div>
  );
}

/* ── Hire Modal (enhanced with customerCarType) ───────────────────────── */
function HireModal({ driver, onClose, onSuccess, carYear, carModel, fuelType, fromLocation, toLocation, referenceRates }) {
  const { t } = useLanguage();
  const auth = getAuth();
  const [form, setForm] = useState({
    date: "", time: "", duration: "8hrs",
    pickupAddress: "", tripPurpose: "",
    customerPhone: auth?.phone || "",
    customerName: auth?.name || auth?.displayName || "",
    customerCarType: "",
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [detecting, setDetecting] = useState(false);
  const [detectErr, setDetectErr] = useState("");

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const formatPriceWithPaisa = (price) => {
    if (!price) return "₹0";
    return `₹${Math.round(price).toLocaleString("en-IN")}`;
  };

  // Slab for the chosen duration. Outstation defaults to the 24-hour slab, which is
  // what the old single per-day outstation rate became.
  const durKey = form.duration === "4hrs" ? "local4hr" : form.duration === "outstation" ? "outstation24hr" : "local8hr";
  const driverRate = driver[durKey]
    || (durKey === "local4hr" ? driver.halfDayRate
      : durKey === "outstation24hr" ? (driver.outstationDay || driver.outstationRate)
      : driver.dailyRate)
    || null;
  const typical = referenceRates?.[durKey] || null;
  const partnerPrice = driverRate || typical || null;

  // Seed the pickup address from the HIRE trip context. This used to read the cab
  // flow's sramika_ss_home_fromLocation, so a car search silently prefilled a hire
  // form with a route the customer never chose for hiring.
  useEffect(() => {
    try {
      const cachedFrom = loadHireTripContext().fromLocation;
      if (cachedFrom) {
        const text = locationText(cachedFrom);
        if (text) {
          setForm(p => ({ ...p, pickupAddress: text }));
        }
      }
    } catch (e) {
      console.error("Failed to parse cachedFrom", e);
    }
  }, []);

  async function handleAutoDetectAddress() {
    setDetecting(true);
    setDetectErr("");
    try {
      await loadGoogleMaps().catch(() => {}); // Maps loads on demand (utils/googleMaps)
      const res = await detectCityFromGPS();
      if (res && res.address) {
        setForm(p => ({ ...p, pickupAddress: res.address }));
      } else {
        setDetectErr("Could not retrieve precise address details.");
      }
    } catch (err) {
      setDetectErr("Location permission denied or geocoding failed.");
    } finally {
      setDetecting(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.date) return setError("Date is required");
    if (!form.time) return setError("Time is required");
    if (!form.customerPhone || !/^[6-9]\d{9}$/.test(form.customerPhone)) return setError("Your phone number is required");
    if (!form.customerCarType) return setError("Please select your car type");

    setSending(true);
    try {
      await apiRequest("/hire-requests", {
        method: "POST",
        body: JSON.stringify({
          driverId: driver.driverId,
          carYear,
          carModel,
          fuelType,
          fromLocation: (fromLocation?.label || fromLocation || null),
          toLocation: (toLocation?.label || toLocation || null),
          fare: partnerPrice,
          ...form,
        }),
      });
      onSuccess();
    } catch (err) {
      setError(err?.error || err?.message || "Failed to send request");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-[#1A1A2E]">{t("hireDriverTitle").replace("Driver", driver.driverName)}</p>
            <p className="text-[10px] text-[#6B7280]">{t("driverWillCome")}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[#F5F5F5] flex items-center justify-center text-[#6B7280] hover:bg-[#E5E7EB]">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {error && <div className="text-[11px] text-[#DC3545] bg-red-50 rounded-lg px-3 py-2">{error}</div>}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="form-label">{t("date")} <span className="text-[#DC3545]">*</span></label>
              <input type="date" className="input text-xs h-[36px] rounded-[10px]" value={form.date} min={new Date().toISOString().split("T")[0]}
                onChange={e => set("date", e.target.value)} />
            </div>
            <div>
              <label className="form-label">{t("time")} <span className="text-[#DC3545]">*</span></label>
              <input type="time" className="input text-xs h-[36px] rounded-[10px]" value={form.time} onChange={e => set("time", e.target.value)} />
            </div>
          </div>

          <div>
            <label className="form-label">{t("duration")} <span className="text-[#DC3545]">*</span></label>
            <div className="flex gap-2 mt-1">
              {[{k: "4hrs", l: "Half Day"}, {k: "8hrs", l: "Full Day"}, {k: "outstation", l: "Outstation"}].map(({ k, l }) => (
                <button key={k} type="button"
                  className={`flex-1 h-[34px] rounded-[10px] text-[10px] font-bold border transition-all ${form.duration === k ? "bg-[#7BAE00] text-white border-transparent shadow-sm shadow-[#7BAE00]/10" : "border-[#E5E7EB] text-[#6B7280] bg-white"}`}
                  onClick={() => set("duration", k)}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="form-label">{t("yourCarType")} <span className="text-[#DC3545]">*</span></label>
            <div className="flex gap-2 mt-1">
              {vehicleTypes().map(v => (
                <button key={v} type="button"
                  className={`flex-1 h-[34px] rounded-[10px] text-[10px] font-bold border transition-all ${form.customerCarType === v ? "bg-[#7BAE00] text-white border-transparent shadow-sm shadow-[#7BAE00]/10" : "border-[#E5E7EB] text-[#6B7280] bg-white"}`}
                  onClick={() => set("customerCarType", v)}>
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="form-label mb-0">{t("pickupAddress")}</label>
              <button
                type="button"
                onClick={handleAutoDetectAddress}
                disabled={detecting}
                className="text-[9px] text-[#7BAE00] font-extrabold flex items-center gap-[3px] hover:text-[#689300] transition-colors"
              >
                {detecting ? (
                  <span className="w-2 h-2 border border-[#7BAE00] border-t-transparent rounded-full animate-spin inline-block" />
                ) : (
                  <Icon name="mapPin" size={10} strokeWidth={2.5} />
                )}
                Auto Detect
              </button>
            </div>
            <textarea className="input text-xs rounded-[10px] focus:border-[#7BAE00]" rows={2} placeholder="Where should the driver come?" value={form.pickupAddress}
              onChange={e => set("pickupAddress", e.target.value)} />
            {detectErr && <p className="text-[8.5px] text-[#DC3545] mt-1">{detectErr}</p>}
          </div>

          <div>
            <label className="form-label">{t("yourPhone")} <span className="text-[#DC3545]">*</span></label>
            <input className="input text-xs h-[36px] rounded-[10px]" inputMode="numeric" maxLength={10} value={form.customerPhone}
              onChange={e => set("customerPhone", e.target.value.replace(/\D/g, "").slice(0, 10))} />
          </div>

          <div>
            <label className="form-label">{t("yourName")}</label>
            <input className="input text-xs h-[36px] rounded-[10px]" value={form.customerName} placeholder="Your name"
              onChange={e => set("customerName", e.target.value)} />
          </div>

          <div>
            <label className="form-label">{t("tripPurpose")}</label>
            <input className="input text-xs h-[36px] rounded-[10px]" value={form.tripPurpose} placeholder="e.g. Airport pickup, Wedding, etc."
              onChange={e => set("tripPurpose", e.target.value)} />
          </div>

          {/* Rate Info */}
          <div className="bg-[#F6FAEC] border border-[#7BAE00]/20 rounded-xl p-3">
            <p className="text-[9px] text-[#7B8794] leading-none mb-[4px]">{t('partnersPrice')}</p>
            <p className="text-[22px] font-bold text-[#1F2937] leading-none">{partnerPrice != null ? formatPriceWithPaisa(partnerPrice) : "-"}</p>
            {typical != null && partnerPrice != null && typical > partnerPrice && (
              <div className="mt-[3px]">
                <p className="text-[10px] text-[#9CA3AF] font-medium leading-none">{t('typicalFareLabel').replace('{value}', Math.round(typical).toLocaleString("en-IN"))}</p>
                <p className="text-[10px] text-[#7BAE00] font-medium leading-none mt-[2px]">{t('lessThanTypicalFare').replace('{amount}', Math.round(typical - partnerPrice).toLocaleString("en-IN"))}</p>
              </div>
            )}
            <p className="text-[9px] text-[#7B8794] mt-[4px]">{t("youPayDriverDirectly")}</p>
          </div>

          <button type="submit" disabled={sending}
            className="w-full h-[40px] rounded-full bg-[#7BAE00] hover:bg-[#689300] text-white text-[11px] font-bold shadow-sm shadow-[#7BAE00]/10 transition-all flex items-center justify-center active:scale-[0.98]">
            {sending ? "Sending Request…" : "Send Hire Request"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ── Rating Modal ─────────────────────────────────────────────────────── */
// Extracted to components/HireRatingModal.jsx so the driver profile reuses the exact
// same sheet. Imported at the top of this file as HireRatingModal.

/* ── Customer Booking Card ────────────────────────────────────────────── */
/* A request from the older hire flow (/hire-requests), for the customers who still
   have one. Its words are the reader's; the flow itself is unchanged. */
function CustomerBookingCard({ req, onRate }) {
  const { t, lang } = useLanguage();
  const ln = useLocalNames();
  const isPending = req.status === "PENDING";
  const isAccepted = req.status === "ACCEPTED";
  const dMap = { "4hrs": t("hm_halfDay"), "8hrs": t("hm_fullDay"), "outstation": t("hm_ttOut") };
  const tripDatePassed = new Date(req.date) < new Date();
  const statusWord = isPending ? t("hm_reqPending")
    : isAccepted ? t("hm_reqAccepted")
    : ["REJECTED", "DECLINED"].includes(req.status) ? t("hm_reqRejected")
    : req.status;
  const when = (() => {
    const d = new Date(`${req.date}T${req.time || "00:00"}`);
    return Number.isNaN(d.getTime())
      ? [req.date, req.time].filter(Boolean).join(" ")
      : dateTimeText(d, lang);
  })();

  return (
    <div className={`bg-white rounded-2xl border border-[#E5E7EB] p-3 shadow-[0_2px_10px_rgba(15,23,42,0.04)] border-l-[4px] ${isPending ? "border-l-[#F59E0B]" : isAccepted ? "border-l-[#7BAE00]" : "border-l-[#EF4444]"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11.5px] font-bold text-[#1F2937]">{ln(req.driverName, "person") || t("hm_grpDriver")}</p>
          <p className="text-[9.5px] text-[#64748B] mt-[2px] flex items-center gap-1"><Icon name="calendar" size={11} strokeWidth={2.2} /> {when}</p>
          <p className="text-[9.5px] text-[#64748B] flex items-center gap-1"><Icon name="clock" size={11} strokeWidth={2.2} /> {dMap[req.duration] || req.duration}</p>
          {req.customerCarType && <p className="text-[9.5px] text-[#64748B] flex items-center gap-1 mt-[2px]"><Icon name="car" size={11} strokeWidth={2.2} /> {req.customerCarType}</p>}
          {req.pickupAddress && <p className="text-[9.5px] text-[#64748B] flex items-center gap-1 mt-[2px]"><Icon name="mapPin" size={11} strokeWidth={2.2} /> {req.pickupAddress}</p>}
        </div>
        <span className={`text-[9.5px] font-bold px-[8px] py-[3px] rounded-full shrink-0 ${
          isPending ? "bg-amber-50 text-amber-800 border border-amber-200/50" : isAccepted ? "bg-[#F1F7E3] text-[#3F5E00] border border-[#D6E6B0]" : "bg-red-50 text-red-800 border border-red-200/50"
        }`}>{statusWord}</span>
      </div>

      {/* Show driver contact on ACCEPTED */}
      {isAccepted && req.driverPhone && (
        <div className="mt-2 bg-[#F8FBF1] border border-[#D6E6B0] rounded-xl p-2 flex items-center gap-2">
          <span className="text-[11px] font-bold text-[#1F2937]">{req.driverPhone}</span>
          <a href={`tel:+91${req.driverPhone}`}
            className="ml-auto min-h-[40px] px-4 rounded-[11px] bg-[#7BAE00] text-white text-[11px] font-bold inline-flex items-center gap-1.5 active:scale-[0.98]">
            <Icon name="phone" size={13} stroke="#FFFFFF" fill="#FFFFFF" strokeWidth={1.4} />{t("hm_call")}
          </a>
        </div>
      )}

      {/* Rate button for completed trips */}
      {isAccepted && tripDatePassed && !req.rated && (
        <button onClick={() => onRate(req)}
          className="mt-2.5 w-full min-h-[40px] rounded-xl bg-amber-50 text-amber-800 text-[10.5px] font-bold border border-amber-200 flex items-center justify-center gap-1.5 active:scale-[0.98]">
          <Icon name="star" size={12} strokeWidth={2.5} /> {t("hm_rateThisDriver")}
        </button>
      )}
      {req.rated && (
        <p className="mt-2 text-[10px] text-[#5E8700] font-bold flex items-center gap-1"><Icon name="check" size={11} strokeWidth={3} /> {t("hm_rated")}</p>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════════ */
export default function AltingDriversList() {
  usePageMeta(
    "Hire a Driver in Vijayawada for Your Own Car | Sramika",
    "Hire a driver for your own car in Vijayawada - by the hour, the day or for an outstation trip. Duty rates shown upfront. Call the driver directly."
  );
  const navigate = useNavigate();
  const { t, lang, setLang } = useLanguage();
  // Cities, areas and names in the reader's script (utils/localNames).
  const ln = useLocalNames();
  const location = useLocation();
  const { user } = useAuth();
  const auth = getAuth();
  const effectiveUser = user || auth;
  const isPartner = ["CAB_OWNER", "OWNER", "ALTING_DRIVER", "ALTING_DRIVER"].includes(effectiveUser?.role);
  const isLoggedIn = !!effectiveUser?.phone;

  /* ── State ───────────────────────────────────────────────────────────── */
  const [drivers, setDrivers] = useState([]);
  // Loading until the first answer: an empty list before it said "no drivers" for a
  // moment on every Cars ⇄ Drivers switch (26 Oct 2026).
  const [loading, setLoading] = useState(true);
  // The saved city, read as the listed city it names in whatever script it was saved.
  const [fromLocation, setFromLocation] = useState(() => {
    const saved = localStorage.getItem("sramika_selected_city") || "";
    return canonicalCity(saved) || saved;
  });
  const [toLocation, setToLocation] = useState("");
  const [carYear, setCarYear] = useState("");
  const [carModel, setCarModel] = useState("");
  const [fuelType, setFuelType] = useState("");

  // The canonical city the picker resolved, e.g. "Vijayawada" for a Gollapudi pick.
  const [selectedCity, setSelectedCity] = useState("");
  // The schedule, which is what actually prices a hire. Read from the same context
  // as the locations so a refresh keeps the quote rather than blanking it.
  const [pickupDateTime, setPickupDateTime] = useState("");
  const [dropDateTime, setDropDateTime] = useState("");
  // Round trip or dropping - only said in the trip's summary line; it does not change
  // the fare, which the profile reads from the saved trip for itself.
  const [tripKind, setTripKind] = useState("");

  // Two values, deliberately separate. The customer chose a locality; drivers are
  // registered against canonical cities from a fixed list. Querying the locality
  // matches nobody, and labelling the UI with the canonical city would misreport
  // what they actually asked for.
  const displayCity = (typeof fromLocation === "string" ? fromLocation.split(",")[0]?.trim() : "") ||
    (fromLocation?.value ? fromLocation.value.split(",")[0]?.trim() : "");
  // Falls back to the locality when no canonical city is known — that is the old
  // behaviour, so an unmatched location is no worse off than before.
  const searchCity = selectedCity || displayCity;

  /* Which drivers appear is decided by distance from the pickup, not by the city name
     it resolved to — the same rule the cab listing follows. The city is still sent
     because it chooses the rate card and the gate; it no longer chooses who is seen.
     Without a pickup point there is nothing to measure from and it stays a city query. */
  const pickupPoint =
    (typeof fromLocation === "object" && Number.isFinite(fromLocation?.location?.lat) && Number.isFinite(fromLocation?.location?.lng))
      ? { lat: fromLocation.location.lat, lng: fromLocation.location.lng }
      : null;
  const pickupKeyForFetch = pickupPoint ? `${pickupPoint.lat},${pickupPoint.lng}` : "";

  const [hireDriver, setHireDriver] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [hireSuccess, setHireSuccess] = useState(false);
  const [referenceRates, setReferenceRates] = useState(null);
  // The city's km boundaries and night window. Distance decides Local vs Outstation,
  // so a quote computed without the gate would price a 200 km run as a city job.
  const [hireGate, setHireGate] = useState(null);
  const [tripDistanceKm, setTripDistanceKm] = useState(null);
  // Google's estimate for the drive back, billed as part of the booking.
  const [returnDurationMins, setReturnDurationMins] = useState(0);

  // The one gate for showing money on this page. Note fromLocation alone is not
  // enough — it defaults to the stored city name, which is not a route.
  const hasTripContext = hasHireTripContext(fromLocation, toLocation);

  // No route, no booking: send the customer to set one and bring them back to the
  // driver they were looking at. Mirrors the cab detail page's "Select Route to
  // Book", which redirects rather than opening a booking surface without a price.
  const openHireOrPickRoute = (driver) => {
    if (!hasTripContext) {
      // Setting the trip needs no login - only Call does (19 Oct 2026).
      navigate("/hire-location-picker", { state: { returnToDriver: driver.driverId, returnState: { fromLocation, toLocation } } });
      return;
    }
    // With a route set, go to the driver's profile — that is where "Hire this driver"
    // records the lead and reveals the number (the reference's journey), rather than
    // the old in-list modal. Trip context rides along so the profile can price it.
    navigate(`/drivers/${encodeURIComponent(driver.driverId)}`, {
      state: { fromLocation, toLocation, selectedCity, pickupDateTime, dropDateTime },
    });
  };

  // "Call to Book" on the card: with a route already set, place the call directly —
  // record the lead, reveal the number and open the dialer — instead of routing to the
  // profile first. Without a route, fall back to the picker gate (openHireOrPickRoute).
  const [callingId, setCallingId] = useState(null);
  const handleCallToBook = async (driver) => {
    if (!hasTripContext) { openHireOrPickRoute(driver); return; }
    track("call_tapped", { type: "driver", method: "card", driverId: driver.driverId });
    // App calls (mock AppCall_2): no login - Sramika's number and a code.
    {
      setCallingId(driver.driverId);
      const q = computeHireFare({ pickupDateTime, dropDateTime, distanceKm: tripDistanceKm, returnDurationMins, tripType: tripKind, rates: driver, gate: hireGate || undefined });
      const dialing = await startAppCall({ kind: "driver", driverId: driver.driverId, page: "drivers-list", body: {
        customerName: user?.name || user?.displayName || null, fromLocation: locationText(fromLocation), dropLocation: locationText(toLocation),
        tripType: tripKind || null, distanceKm: tripDistanceKm || null, pickupDateTime, dropDateTime,
        quotedFare: q?.total ?? null, quotedFareBreakdown: q?.breakdown ?? null, ...readTripExtras("HIRE") } });
      setCallingId(null);
      if (dialing === true) return;
    }
    if (!user) {
      navigate("/login", { state: { redirectTo: `/drivers/${encodeURIComponent(driver.driverId)}`, payload: { returnToDriver: driver.driverId, returnState: { fromLocation, toLocation } } } });
      return;
    }
    setCallingId(driver.driverId);
    try {
      // Desk staff: for themselves, or for a customer (mock CallBook_1).
      const ob = await askOnBehalf();
      if (ob === ON_BEHALF_CANCELLED) return;
      const quoted = computeHireFare({ pickupDateTime, dropDateTime, distanceKm: tripDistanceKm, returnDurationMins, tripType: tripKind, rates: driver, gate: hireGate || undefined });
      // A call Sramika connects may cost money: the gate asks first, then calls again.
      const res = await callWithWalletGate(({ confirmPaid }) => apiRequest(`/alting-drivers/${encodeURIComponent(driver.driverId)}/call`, {
        method: "POST",
        body: JSON.stringify({
          confirmPaid,
          ...onBehalfBody(ob),
          customerName: user?.name || user?.displayName || null,
          fromLocation: locationText(fromLocation),
          dropLocation: locationText(toLocation),
          // One way or round, and the road distance, for the lead the admin reads.
          tripType: tripKind || null,
          distanceKm: tripDistanceKm || null,
          pickupDateTime,
          dropDateTime,
          quotedFare: quoted?.total ?? null,
          quotedFareBreakdown: quoted?.breakdown ?? null,
          // Passengers, luggage and the note from the trip form, for the lead.
          ...readTripExtras("HIRE"),
        }),
      }));
      if (res?.success) track("call_placed", { type: "driver", method: "card", driverId: driver.driverId, leadId: res?.leadId });
      if (res?.success && res?.partnerPhone) {
        window.location.href = `tel:+91${String(res.partnerPhone).slice(-10)}`;
      } else {
        // Anything unexpected → the profile, where the two-step reveal + error UI lives.
        navigate(`/drivers/${encodeURIComponent(driver.driverId)}`, { state: { fromLocation, toLocation, selectedCity, pickupDateTime, dropDateTime } });
      }
    } catch (err) {
      if (showBusinessLimit(err)) return; // one business call every 6 hours (mock B2BLimit_1)
      // Choosing not to pay for the call leaves the customer where they are.
      if (err?.code !== WALLET_GATE_CANCELLED) {
        navigate(`/drivers/${encodeURIComponent(driver.driverId)}`, { state: { fromLocation, toLocation, selectedCity, pickupDateTime, dropDateTime } });
      }
    } finally {
      setCallingId(null);
    }
  };

  // Filters - see applyDriverFilters above.
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [driverFilters, setDriverFilters] = useState(NO_FILTERS);
  // The order and the fare band (mock Fix_C changes 1 and 8): not filters of who a driver
  // is, so kept apart from driverFilters.
  const [driverSort, setDriverSort] = useState("RECOMMENDED");
  const [driverBudget, setDriverBudget] = useState("");
  const setFilter = (key, value) => setDriverFilters((prev) => ({ ...prev, [key]: value }));

  /* Restore trip context from session on a cold load or refresh. Router state wins
     when present — the effect below runs after this and overwrites — but on a
     refresh there is no router state, and this is the only thing that survives. */
  useEffect(() => {
    if (location.state?.fromLocation || location.state?.toLocation) return;
    const stored = loadHireTripContext();
    if (stored.fromLocation) setFromLocation(stored.fromLocation);
    if (stored.toLocation) setToLocation(stored.toLocation);
    // Without this the list silently reverts to comma-splitting one reload later.
    if (stored.selectedCity) setSelectedCity(stored.selectedCity);
    if (stored.pickupDateTime) setPickupDateTime(stored.pickupDateTime);
    if (stored.dropDateTime) setDropDateTime(stored.dropDateTime);
    if (stored.tripType) setTripKind(stored.tripType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Restore state from navigation */
  useEffect(() => {
    const state = location.state;
    if (!state) return;
    if (state.fromLocation) setFromLocation(state.fromLocation);
    if (state.toLocation) setToLocation(state.toLocation);
    // The picker resolved this; using it is the whole point of sending it.
    if (state.selectedCity) setSelectedCity(state.selectedCity);
    if (state.pickupDateTime) setPickupDateTime(state.pickupDateTime);
    if (state.dropDateTime) setDropDateTime(state.dropDateTime);
    if (state.tripType) setTripKind(state.tripType);

    // Stage 2 setup: hold car details if returned from HireLocationPicker
    if (state.carYear) setCarYear(state.carYear);
    if (state.carModel) setCarModel(state.carModel);
    if (state.fuelType) setFuelType(state.fuelType);
    // A name found here from the Cars search ("no car by this name - found in Drivers").
    if (typeof state.searchQuery === "string") setSearchQuery(state.searchQuery);

    if (state.fromLocation || state.toLocation || state.carYear) navigate(".", { replace: true, state: { hireDriverId: state.hireDriverId } });
  }, [location.state, navigate]);

  // Customer bookings
  const [showMyBookings, setShowMyBookings] = useState(false);
  const [myBookings, setMyBookings] = useState([]);
  const [myBookingsLoading, setMyBookingsLoading] = useState(false);

  // Rating
  const [ratingBooking, setRatingBooking] = useState(null);
  const [ratingSuccess, setRatingSuccess] = useState(false);

  // Auto rating popup
  const [autoRatingChecked, setAutoRatingChecked] = useState(false);

  /* ── Open Hire modal if navigated from driver profile with state ── */
  useEffect(() => {
    const hireId = location.state?.hireDriverId;
    if (hireId) {
      apiRequest(`/alting-drivers/${encodeURIComponent(hireId)}`)
        .then((r) => {
          if (r.data) {
            if (r.data.city) {
              setFromLocation(r.data.city);
              localStorage.setItem("sramika_selected_city", r.data.city);
            }
            // Second gate. The profile no longer sends hireDriverId without a
            // route, but a back-navigation or a stale link still can, and this
            // path must not become the way a modal opens with no price.
            if (hasStoredHireTripContext()) {
              setHireDriver(r.data);
            } else {
              navigate("/hire-location-picker", {
                replace: true,
                state: { returnToDriver: hireId, returnState: {} }
              });
              return;
            }
            // Clear location state from history
            navigate(location.pathname, { replace: true, state: {} });
          }
        })
        .catch(() => {});
    }
  }, [location.state?.hireDriverId, location.pathname, navigate]);

  /* ── Trip distance ───────────────────────────────────────────────────
     Not a price input — a hire is sold by time — but it decides which slab the
     quote comes from, so a missing value would price every long trip as local. */
  useEffect(() => {
    const fromVal = fromLocation?.value;
    const toVal = toLocation?.value;
    if (!fromVal || !toVal) { setTripDistanceKm(null); return; }
    let active = true;
    // Two lookups: the outbound leg for distance, and the reverse for how long the
    // driver needs to get home. The return leg is billed — the driver is not free at
    // the drop — so it must come from a real estimate, not a doubling of the outbound.
    Promise.all([
      calculateTripMetrics(fromVal, toVal),
      calculateTripMetrics(toVal, fromVal),
    ])
      .then(([out, back]) => {
        if (!active) return;
        // Null, never zero: zero would read as "a local trip" and quote the wrong slab.
        setTripDistanceKm(out?.distanceKm || null);
        setReturnDurationMins(back?.durationMinutes || 0);
      })
      .catch(() => { if (active) { setTripDistanceKm(null); setReturnDurationMins(0); } });
    return () => { active = false; };
  }, [fromLocation, toLocation]);

  /* ── Fetch drivers ───────────────────────────────────────────────── */
  useEffect(() => {
    if (!searchCity) { setLoading(false); return; }
    let alive = true;
    const geo = pickupPoint
      // No radiusKm: the server applies the city's admin-set listing radius.
      ? `&lat=${encodeURIComponent(pickupPoint.lat)}&lng=${encodeURIComponent(pickupPoint.lng)}`
      : "";
    const path = `/alting-drivers?city=${encodeURIComponent(searchCity)}${geo}`;
    const show = (r) => {
      setDrivers(r?.data || []);
      setReferenceRates(r?.referenceRates || null);
      setHireGate(r?.gate || null);
    };

    /* A list seen in the last 45 minutes shows at once - no spinner on Cars ⇄ Drivers -
       and is asked for again only when it is more than a minute old: coming back to the
       app no longer downloads the whole list every time (utils/listCache). */
    const kept = cachedList(path);
    if (kept) { show(kept); setLoading(false); }

    const fetchDrivers = (quiet = false) => {
      if (isFresh(path)) return;
      if (!quiet && !cachedList(path)) setLoading(true);
      fetchList(path)
        .then((r) => {
          if (!alive) return;
          show(r);
          // The Cars tab's list, ready before it is opened.
          prefetchList(carsListPath(searchCity));
        })
        .catch(() => { if (alive && !cachedList(path)) setDrivers([]); })
        .finally(() => { if (alive) setLoading(false); });
    };

    fetchDrivers(!!kept);

    // Refreshed when coming back to this page/tab - quietly, and not within a minute.
    const handleVisibility = () => { if (document.visibilityState === "visible") fetchDrivers(true); };
    const handleFocus = () => { fetchDrivers(true); };

    window.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleFocus);

    return () => {
      alive = false;
      window.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCity, pickupKeyForFetch]);

  /* ── Fetch customer bookings ─────────────────────────────────────── */
  useEffect(() => {
    if (!isLoggedIn) return;
    setMyBookingsLoading(true);
    apiRequest("/hire-requests/customer")
      .then((r) => setMyBookings(r.data || []))
      .catch(() => setMyBookings([]))
      .finally(() => setMyBookingsLoading(false));
  }, [isLoggedIn, hireSuccess]);

  /* ── Auto-popup rating for completed trips ───────────────────────── */
  useEffect(() => {
    if (autoRatingChecked || myBookings.length === 0) return;
    setAutoRatingChecked(true);
    const unrated = myBookings.find(b =>
      b.status === "ACCEPTED" && new Date(b.date) < new Date() && !b.rated
    );
    if (unrated) {
      // Small delay so page loads first
      setTimeout(() => setRatingBooking(unrated), 800);
    }
  }, [myBookings, autoRatingChecked]);

  /* ── Client-side filter (Fuse.js) ────────────────────────────────── */
  const { filteredDrivers, matchReasons } = useMemo(() => {
    let result = drivers;
    let matchReasonsMap = {};

    // A city typed in another script is searched as its English name ("గుంటూరు" -> "guntur").
    // Dots and commas are spaces, so initials typed "s.k.name" find "S K Name" (Oct 2026).
    // A sentence the box understood is a request, not words to find (mock Four_1 change 7).
    const q = cityWordsInEnglish((parseQuery(searchQuery) ? "" : searchQuery).trim().toLowerCase().replace(/[.,]+/g, " ").replace(/\s+/g, " ").trim());
    if (q.length > 0) {
      const searchIndex = result.map(d => ({
        driverId: d.driverId || d.phone,
        driverName: d.driverName || "",
        city: d.city || "",
        vehicleTypes: d.vehicleTypes || [],
        languagesSpoken: Array.isArray(d.languagesSpoken)
          ? d.languagesSpoken
          : (d.languagesSpoken || "").split(",").map(s => s.trim()),
        availability: d.availability || [],
        transmission: d.automaticExperience ? "Automatic Auto" : "Manual",
        rating: d.averageRating ? `${d.averageRating} stars` : "",
        local4hr: d.local4hr ? `${d.local4hr}` : d.halfDayRate ? `${d.halfDayRate}` : "",
        local8hr: d.local8hr ? `${d.local8hr}` : d.dailyRate ? `${d.dailyRate}` : ""
      }));

      const fuse = new Fuse(searchIndex, {
        threshold: 0.4,
        includeScore: true,
        includeMatches: true,
        minMatchCharLength: 2,
        keys: [
          { name: "driverName", weight: 0.35 },
          { name: "languagesSpoken", weight: 0.25 },
          { name: "experience", weight: 0.20 },
          { name: "city", weight: 0.20 }
        ]
      });

      const results = fuse.search(q);
      const matchedIds = new Set();
      // Why each driver matched, as a dictionary word.
      const keyLabels = {
        driverName: "hm_mName",
        languagesSpoken: "hm_mLang",
        experience: "hm_experience",
        city: "hm_mCity"
      };

      results.forEach(res => {
        matchedIds.add(res.item.driverId);
        if (res.matches && res.matches.length > 0) {
          matchReasonsMap[res.item.driverId] = keyLabels[res.matches[0].key] || "hm_mProfile";
        }
      });
      result = result.filter(d => matchedIds.has(d.driverId || d.phone));
    }

    result = applyDriverFilters(result, driverFilters);

    // ── 5-Tier Sort ──────────────────────────────────────────────────────────
    // Tier 1: Availability (AVAILABLE first)
    // Tier 2: driver_score >= 100 (boosted)
    // Tier 3: Average rating DESC
    // Tier 4: driver_score DESC
    // Tier 5: Created At ASC (longest on platform first)
    const sorted = [...result].sort((a, b) => {
      // 1. Availability
      const avA = (a.availabilityStatus || a.availability_status || "AVAILABLE") === "AVAILABLE" ? 1 : 0;
      const avB = (b.availabilityStatus || b.availability_status || "AVAILABLE") === "AVAILABLE" ? 1 : 0;
      if (avA !== avB) return avB - avA;

      // 1b. "Sramika Recommended" (mock Rec_1): this list is everyone within reach, not by distance.
      const rcA = Date.parse(a.recommendedUntil || 0) > Date.now() ? 1 : 0, rcB = Date.parse(b.recommendedUntil || 0) > Date.now() ? 1 : 0;
      if (rcA !== rcB) return rcB - rcA;

      // 2. Score >= 100 boost
      const scoreA = a.driver_score ?? 0;
      const scoreB = b.driver_score ?? 0;
      const boostA = scoreA >= 100 ? 1 : 0;
      const boostB = scoreB >= 100 ? 1 : 0;
      if (boostA !== boostB) return boostB - boostA;

      // 3. Rating DESC
      const ratA = a.averageRating ?? 0;
      const ratB = b.averageRating ?? 0;
      if (ratA !== ratB) return ratB - ratA;

      // 4. Score DESC
      if (scoreA !== scoreB) return scoreB - scoreA;

      // 5. Created At ASC
      const timeA = new Date(a.createdAt || 0).getTime();
      const timeB = new Date(b.createdAt || 0).getTime();
      return timeA - timeB;
    });

    return { filteredDrivers: sorted, matchReasons: matchReasonsMap };
  }, [drivers, driverFilters, searchQuery]);

  // The search history, same as the Cars list: one entry per settled search, with
  // how many drivers it found. See utils/searchLog.js.
  useEffect(() => {
    if (!searchQuery.trim() || loading) { cancelSearchLog("HIRE"); return; }
    logSearch("HIRE", { query: searchQuery, results: filteredDrivers.length, mode: filteredDrivers.length ? "TEXT" : "NONE" });
  }, [searchQuery, loading, filteredDrivers.length]);

  const activeFilterCount = Object.values(driverFilters).filter(Boolean).length;

  function clearFilters() {
    setDriverFilters(NO_FILTERS);
    setDriverBudget("");
  }

  /* Each driver's fare for the trip set, the fare band, and the chosen order. The
     default order is the list's own (availability, score, rating - above). */
  const faresByDriver = useMemo(() => {
    const m = new Map();
    if (!hasTripContext) return m;
    for (const d of filteredDrivers) {
      m.set(d, computeHireFare({ pickupDateTime, dropDateTime, distanceKm: tripDistanceKm, returnDurationMins, tripType: tripKind, rates: d, gate: hireGate || undefined }));
    }
    return m;
  }, [filteredDrivers, hasTripContext, pickupDateTime, dropDateTime, tripDistanceKm, returnDurationMins, tripKind, hireGate]);
  const shownDrivers = useMemo(() => {
    const fareTotal = (d) => { const f = faresByDriver.get(d); return f && !f.blocked && f.total > 0 ? f.total : null; };
    let list = driverBudget ? filteredDrivers.filter((d) => inDriverBudget(driverBudget, fareTotal(d))) : filteredDrivers;
    const years = (d) => expFloor(d.drivingExperience ?? d.experienceYears) ?? -1;
    const by = {
      PRICE_LOW: (a, b) => (fareTotal(a) ?? Infinity) - (fareTotal(b) ?? Infinity),
      EXPERIENCE: (a, b) => years(b) - years(a),
      RATING: (a, b) => (b.averageRating ?? 0) - (a.averageRating ?? 0) || (b.ratingCount ?? 0) - (a.ratingCount ?? 0),
      ONLINE: (a, b) => (isAvailableNow(b.availabilityStatus) ? 1 : 0) - (isAvailableNow(a.availabilityStatus) ? 1 : 0) || years(b) - years(a),
      NEAREST: (a, b) => (Number.isFinite(Number(a.distanceFromPickupKm)) ? Number(a.distanceFromPickupKm) : Infinity)
        - (Number.isFinite(Number(b.distanceFromPickupKm)) ? Number(b.distanceFromPickupKm) : Infinity),
    }[driverSort];
    if (by) list = [...list].sort(by);
    return list;
  }, [filteredDrivers, faresByDriver, driverBudget, driverSort]);

  /* ── Rating success handler ──────────────────────────────────────── */
  function handleRatingSuccess() {
    // Mark the booking as rated locally
    setMyBookings(prev => prev.map(b =>
      b.requestId === ratingBooking.requestId ? { ...b, rated: true } : b
    ));
    setRatingBooking(null);
    setRatingSuccess(true);
    setTimeout(() => setRatingSuccess(false), 4000);
  }

  /* ── The trip, in one card ───────────────────────────────────────── */
  const openTripForm = () => navigate("/hire-location-picker", { state: { returnTo: "/drivers", target: "from", fromLocation, toLocation } });

  /* A sentence understood by the search box (mock Four_1 change 7): the language asked
     for becomes the filter, and a place opens the trip form with it, the day and the
     time filled in. A driver drives the customer's own car, so car words are not used. */
  const applyIntent = (intent) => {
    if (intent.language) setFilter("language", intent.language);
    const drop = intentDrop(intent);
    if (drop || intent.when) {
      navigate("/hire-location-picker", { state: {
        returnTo: "/drivers", target: "from", fromLocation, toLocation: drop || toLocation,
        ...(intent.tripType ? { tripType: intent.tripType } : {}),
        ...(intent.when ? { pickupDateTime: intent.when } : {}),
      } });
    }
  };
  /* Swap opens the same form with pickup and drop changed round - the form resolves the
     city the new pickup belongs to, which is what the list and the rates are read by. */
  const swapTrip = () => navigate("/hire-location-picker", {
    state: { returnTo: "/drivers", target: "from", fromLocation: toLocation, toLocation: fromLocation },
  });
  // The pickup is a place the customer chose - never the stored city name this page
  // starts from, which is not a pickup.
  const pickupShort = (typeof fromLocation === "object" || hasTripContext) ? shortPlace(fromLocation) : "";
  const dropShort = shortPlace(toLocation);
  const tripMeta = [
    tripWhen(pickupDateTime, t, lang),
    tripKind ? t(tripKind === "ONE_WAY" ? "lp_tabOneWay" : "lp_tabRoundTrip") : "",
    tripDistanceKm > 0 ? fillText(t("hm_km"), { km: Math.round(tripDistanceKm) }) : "",
  ].filter(Boolean).join(" · ");

  /* ── Your recent enquiry: the newest hire enquiry, shown on one visit only ── */
  const { leads: myLeads } = useMyRecentLeads(isLoggedIn);
  const [hiddenEnquiries, setHiddenEnquiries] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const recentEnquiry = useMemo(() => recentEnquiryOf(myLeads, true), [myLeads, hiddenEnquiries]);

  /* ── Chips and the Filters sheet ─────────────────────────────────── */
  const opt = (o) => ({ value: o.value, label: optionLabel(o, t, lang) });
  const withAny = (options) => [{ value: "", label: t("hm_any") }, ...options.map(opt)];
  const chipText = (value, options, name) => {
    const vals = splitValues(value);
    if (!vals.length) return name;
    const first = options.find((o) => o.value === vals[0]);
    const text = first ? optionLabel(first, t, lang) : vals[0];
    return vals.length > 1 ? `${text} +${vals.length - 1}` : text;
  };
  const AVAILABLE_NOW = [{ value: "now", key: "hm_availNow" }];
  /* Every order is offered, always (the user, 26 Oct 2026: "Nearest first" had gone
     missing). One that needs a pickup or a trip not set yet opens the trip form. */
  const sortOptions = DRIVER_SORTS.map(opt);
  const pickSort = (v) => {
    setDriverSort(v);
    const needs = DRIVER_SORTS.find((o) => o.value === v)?.needs;
    if (needs && !(needs === "route" ? hasTripContext : !!pickupPoint)) openTripForm();
  };
  const sortName = t((DRIVER_SORTS.find((o) => o.value === driverSort) || DRIVER_SORTS[0]).key);
  const sheetGroups = [
    { title: t("hm_grpDriver"), rows: [
      { key: "language", label: t("hm_speaks"), options: DRIVER_LANGS.map(opt) },
      { key: "experience", label: t("hm_experience"), options: EXPERIENCE.map(opt) },
      { key: "rating", label: t("hm_rating"), options: RATINGS.map(opt) },
    ] },
    { title: t("hm_grpTrip"), rows: [
      { key: "tripType", label: t("hm_tripType"), options: DRIVER_TRIPS.map(opt) },
      { key: "availability", label: t("hm_availability"), options: AVAILABLE_NOW.map(opt) },
    ] },
  ];
  const chips = [
    { key: "language", icon: <Icon name="globe" size={14} strokeWidth={2.2} />, name: t("hm_mLang"), options: DRIVER_LANGS },
    { key: "experience", icon: <Icon name="award" size={14} strokeWidth={2.2} />, name: t("hm_experience"), options: EXPERIENCE },
    { key: "rating", icon: <Icon name="star" size={14} strokeWidth={2.2} />, name: t("hm_rating"), options: RATINGS },
    { key: "tripType", icon: <Icon name="car" size={14} strokeWidth={2.2} />, name: t("hm_tripType"), options: DRIVER_TRIPS },
  ];

  // Fewer drivers than the city has: a narrowed list, with Show all.
  const narrowed = shownDrivers.length < drivers.length;
  const items = countWords(shownDrivers.length, t, "drivers");
  const resultTitle = narrowed
    ? fillText(t("hm_resFound"), { items })
    : hasTripContext && pickupShort && dropShort
      ? fillText(t("hm_resRoute"), { items, route: ln(`${pickupShort} → ${dropShort}`, "place") })
      : fillText(t("hm_resAvailIn"), { items, city: ln(searchCity, "place") });
  const showAll = () => { clearFilters(); setSearchQuery(""); };

  /* ── The season's trips, priced for a driver ─────────────────────── */
  const seasonCfg = useSeasonTripsConfig();
  const seasonTrips = useMemo(() => (seasonCfg?.slugs || []).map(seasonTrip).filter(Boolean), [seasonCfg]);
  const [tripsOpen, setTripsOpen] = useState(false);
  // A trip from the list starts tomorrow at 6 am and keeps the driver its usual hours;
  // the trip form changes either.
  const tripTimes = (trip) => {
    const start = new Date(); start.setDate(start.getDate() + 1); start.setHours(6, 0, 0, 0);
    const end = new Date(start.getTime() + trip.hours * 3600000);
    const local = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    return { pickupDateTime: local(start), dropDateTime: local(end), tripType: trip.type === "ONE_WAY" ? "ONE_WAY" : "ROUND" };
  };
  const driverPriced = useMemo(() => {
    if (!drivers.length) return {};
    const out = {};
    for (const trip of seasonTrips) {
      const times = tripTimes(trip);
      const oneWayKm = trip.type === "ROUND" || trip.type === "LOCAL" ? trip.km / 2 : trip.km;
      let best = null;
      for (const d of drivers) {
        const f = computeHireFare({ ...times, distanceKm: oneWayKm, returnDurationMins: times.tripType === "ONE_WAY" ? Math.round((trip.km / 50) * 60) : 0, rates: d, gate: hireGate || undefined });
        if (f && !f.blocked && f.total > 0 && (best == null || f.total < best)) best = f.total;
      }
      if (best != null) out[trip.slug] = { price: best, word: tripText(TRIP_TEXT.driverWord, lang) };
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripsOpen, drivers, seasonTrips, hireGate, lang]);
  const pickSeasonTrip = (trip) => {
    const times = tripTimes(trip);
    const to = { value: trip.to, selected: true };
    setFromLocation(SEASON_FROM); setToLocation(to); setSelectedCity(SEASON_CITY);
    setPickupDateTime(times.pickupDateTime); setDropDateTime(times.dropDateTime); setTripKind(times.tripType);
    saveHireTripContext({ fromLocation: SEASON_FROM, toLocation: to, selectedCity: SEASON_CITY, ...times });
    track("season_trip_tapped", { trip: trip.slug, page: "drivers" });
  };

  /* ── The trip in the address, and Share list (mock Fix_A change 10) ── */
  const linkRead = useRef(false);
  useEffect(() => {
    if (linkRead.current) return;
    linkRead.current = true;
    const q = readTripQuery(location.search);
    if (!q) return;
    if (q.from) setFromLocation(q.from);
    if (q.to) setToLocation(q.to);
    if (q.tripType) setTripKind(q.tripType);
    if (q.on) setPickupDateTime(q.on);
    if (q.back) setDropDateTime(q.back);
    if (q.lang && q.lang !== lang) setLang(q.lang);
    if (q.from || q.to) saveHireTripContext({ fromLocation: q.from || undefined, toLocation: q.to || undefined, tripType: q.tripType || undefined, pickupDateTime: q.on || undefined, dropDateTime: q.back || undefined });
    track("trip_link_opened", { page: "drivers" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!linkRead.current) return;
    // The pickup is in the link only once it is a place, not the stored city name.
    const from = typeof fromLocation === "object" ? fromLocation : "";
    syncTripUrl(tripQuery({ from, to: toLocation, tripType: tripKind, on: pickupDateTime, back: dropDateTime, keepBack: true, lang }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromLocation, toLocation, tripKind, pickupDateTime, dropDateTime, lang, location.key]);
  const [linkNote, setLinkNote] = useState(false);
  const shareList = async () => {
    track("trip_link_shared", { page: "drivers" });
    const how = await shareTripLink({ title: "Sramika" });
    if (how === "copied") { setLinkNote(true); setTimeout(() => setLinkNote(false), 2500); }
  };

  /* ══════════════════════════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════════════════════ */
  return (
    <>
      <div className="page-container hm lg:!max-w-[1240px] lg:!px-6" style={{ paddingBottom: "calc(70px + env(safe-area-inset-bottom, 0px))" }}>
        {/* 1. The same header as the Cars tab */}
        <Header
          city={searchCity}
          onCityChange={(c) => {
            localStorage.setItem("sramika_selected_city", c);
            setFromLocation(c);
            // A city picked from the list is a real city: it is what the list is for.
            setSelectedCity(c);
          }}
          onLocate={setFromLocation}
          subtitle={t("nearbyHireDrivers")}
        />

        {/* ── A laptop (mock Laptop_1): the same frame as Cars ── */}
        <DesktopHero
          title={fillText(t("dk_heroDrivers"), { city: localCity(searchCity || "Vijayawada", lang) })}
          sub={t("dk_subDrivers")}
          below={<><TrustRow items={[["shield", t("dk_trustDrivers")], ["rupee", t("dk_trustPrice")], ["phone", t("dk_trustCallDriver")]]} /><CallToBookLine t={t} from="drivers_laptop" /></>}
          search={(
            <DesktopSearchBar onOpen={openTripForm} cta={t("dk_searchDrivers")}
              onCta={() => (hasTripContext ? document.getElementById("drivers-list")?.scrollIntoView({ behavior: "smooth", block: "start" }) : openTripForm())}
              parts={[
                { kind: "pickup", label: t("lp_lblPickup"), value: pickupShort, placeholder: t("lp_phPrecisePickup") },
                { kind: "drop", label: t("lp_lblDrop"), value: hasTripContext ? dropShort : "", placeholder: t("lp_phDropLocation") },
                { icon: TripIcons.calendar(14), label: t("dk_when"), value: tripWhen(pickupDateTime, t, lang), placeholder: t("dk_pickWhen") },
                { icon: TripIcons.swap(14), label: t("dk_tripType"), value: t(tripKind === "ONE_WAY" ? "lp_tabOneWay" : "lp_tabRoundTrip"), narrow: true },
              ]} />
          )}
          aside={searchCity === SEASON_CITY && seasonTrips.length > 0 ? (
            <TripsCard lang={lang} trips={seasonTrips} priced={driverPriced} onAll={() => navigate("/trips")}
              onOpen={(trip) => { pickSeasonTrip(trip); document.getElementById("drivers-list")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} />
          ) : null} />

        <div className="lg:flex lg:items-start lg:gap-6 lg:mt-6">
        <aside className="hidden lg:block w-[270px] shrink-0 sticky top-[80px]">
          <FilterPanel title={t("dk_filters")} clearLabel={t("hm_clearAll")}
            groups={[...sheetGroups, { title: t("hm_price"), rows: [{ key: "budget", label: t("hm_price"), options: DRIVER_BUDGETS.map(opt), disabled: !hasTripContext }] }]}
            values={{ ...driverFilters, budget: driverBudget }}
            sort={{ label: t("hm_sort"), value: driverSort, onChange: pickSort, options: sortOptions }}
            onToggle={(key, value) => (key === "budget" ? setDriverBudget(toggled(driverBudget, value)) : setFilter(key, toggled(driverFilters[key], value)))}
            onClear={clearFilters} />
        </aside>
        <div className="flex-1 min-w-0">
        <div className="mt-2 space-y-2 lg:mt-0">
          {/* Visitors: what logging in gives */}
          {!isLoggedIn && (
            <div className="lg:hidden">
              <GuestBanner city={searchCity} title={t("hm_guestTitle")} sub={t("hm_guestSubDrivers")}
                cta={t("hm_loginJoin")} onLogin={() => navigate("/login", { state: { redirectTo: "/drivers" } })} />
            </div>
          )}

          {/* Signed in: the most recent hire enquiry, asking for the driver's stars and
              a line of feedback - shown on one visit only */}
          {isLoggedIn && recentEnquiry && (
            <RecentEnquiryCard lead={recentEnquiry} t={t}
              onHide={(lead) => { hideEnquiry(lead); setHiddenEnquiries((n) => n + 1); }} />
          )}

          {/* 2. The trip: where, when - one card, one form */}
          <div className="lg:hidden"><WhereCard
            t={t}
            from={pickupShort}
            to={hasTripContext ? dropShort : ""}
            meta={hasTripContext ? tripMeta : ""}
            onOpen={openTripForm}
            onSwap={swapTrip}
          />
          {/* Call to book (mock CallBook_1), one quiet line since mock Home_1. */}
          <CallToBookBar t={t} from="drivers" /></div>

          {/* The season's trips with a driver (mock Fix_C change 3): the lowest driver fare
              for each; a trip fills itself in and lists the drivers for it. */}
          {searchCity === SEASON_CITY && seasonTrips.length > 0 && (
            <div className="lg:hidden pt-1"><TripsRow lang={lang} trips={seasonTrips} priced={driverPriced}
              onOpen={pickSeasonTrip}
              onAll={() => { setTripsOpen(true); track("season_trips_opened", { page: "drivers" }); }} /></div>
          )}

          {/* 3. Search, and the four filters people reach for */}
          <SearchBar
            onSearch={setSearchQuery}
            initialValue={searchQuery}
            onFilterClick={() => setShowFilterModal(true)}
            placeholder={t("sq_phDrivers")}
            filterCount={activeFilterCount}
            page="drivers"
            onIntent={applyIntent}
          />

          <div className="lg:hidden"><ChipRow>
            <QuickChip icon={TripIcons.swap(14)} label={driverSort === "RECOMMENDED" ? t("hm_sort") : sortName}
              active={driverSort !== "RECOMMENDED"} value={driverSort} options={sortOptions} onChange={pickSort} pickLabel={t("hm_sort")} />
            {chips.map((c) => (
              <QuickChip
                key={c.key}
                icon={c.icon}
                label={chipText(driverFilters[c.key], c.options, c.name)}
                active={!!driverFilters[c.key]}
                value={splitValues(driverFilters[c.key])[0] || ""}
                options={withAny(c.options)}
                onChange={(v) => setFilter(c.key, v)}
                onClear={() => setFilter(c.key, "")}
                clearLabel={t("hm_removeFilter")}
                pickLabel={c.name}
              />
            ))}
            {/* A fare needs the trip; until there is one, Budget opens the trip form. */}
            <QuickChip icon={<Icon name="rupee" size={14} strokeWidth={2.2} />} label={chipText(driverBudget, DRIVER_BUDGETS, t("hm_price"))}
              active={!!driverBudget} value={splitValues(driverBudget)[0] || ""}
              options={hasTripContext ? withAny(DRIVER_BUDGETS) : null} onChange={setDriverBudget}
              onClick={hasTripContext ? null : openTripForm}
              onClear={() => setDriverBudget("")} clearLabel={t("hm_removeFilter")} pickLabel={t("hm_price")} />
          </ChipRow></div>

          {/* ── Requests from the older hire flow (signed-in customers who have one) ── */}
          {isLoggedIn && myBookings.length > 0 && (
            <div className="space-y-2">
              <button type="button" onClick={() => setShowMyBookings(!showMyBookings)} aria-expanded={showMyBookings}
                className="w-full flex items-center justify-between px-3 min-h-[44px] rounded-[14px] bg-gradient-to-r from-[#7BAE00] to-[#5E8700] text-white shadow-sm">
                <span className="flex items-center gap-2 text-[11px] font-bold">
                  <Icon name="calendar" size={14} strokeWidth={2.4} />
                  {t("hm_myRequests")}
                  <span className="bg-white/25 text-white text-[9.5px] font-bold px-[7px] py-[2px] rounded-full leading-none">{myBookings.length}</span>
                </span>
                <Icon name="chevronDown" size={14} strokeWidth={2.6} className={`transition-transform ${showMyBookings ? "rotate-180" : ""}`} />
              </button>

              {showMyBookings && (
                <div className="space-y-2 fade-up">
                  {myBookingsLoading ? (
                    <Loading text={t("hm_loading")} />
                  ) : (
                    myBookings.map(b => (
                      <CustomerBookingCard key={b.requestId} req={b} onRate={(booking) => setRatingBooking(booking)} />
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Success notes */}
          {hireSuccess && (
            <div className="rounded-2xl border border-[#D6E6B0] bg-[#F1F7E3] p-3 text-center fade-up">
              <p className="text-[11.5px] font-bold text-[#3F5E00] flex items-center justify-center gap-1.5"><Icon name="checkCircle" size={14} strokeWidth={2.4} /> {t("hm_reqSent")}</p>
              <p className="text-[10px] text-[#475569] leading-snug mt-1">{t("hm_reqSentText")}</p>
              <button className="mt-2 min-h-[36px] px-4 text-[10.5px] font-bold text-[#3F5E00]" onClick={() => setHireSuccess(false)}>{t("hm_ok")}</button>
            </div>
          )}
          {ratingSuccess && (
            <div className="rounded-2xl border border-[#F5D48A] bg-[#FFFBEB] p-3 text-center fade-up">
              <p className="text-[11.5px] font-bold text-[#B45309] flex items-center justify-center gap-1.5"><Icon name="star" size={14} fill="#D97706" stroke="#D97706" /> {t("hm_ratingSent")}</p>
              <p className="text-[10px] text-[#475569] leading-snug mt-1">{t("hm_ratingThanks")}</p>
              <button className="mt-2 min-h-[36px] px-4 text-[10.5px] font-bold text-[#B45309]" onClick={() => setRatingSuccess(false)}>{t("hm_ok")}</button>
            </div>
          )}
        </div>

        {/* 4. The drivers */}
        <div id="drivers-list" className="mt-2 scroll-mt-[84px]">
          {!searchCity ? (
            <ListMessage icon={<Icon name="mapPin" size={22} strokeWidth={2} />} title={t("hm_pickCityDrivers")}>
              <button type="button" onClick={openCityPicker} className={PRIMARY_BTN}>{t("hm_chooseCity")}</button>
            </ListMessage>
          ) : loading ? (
            <Loading text={fillText(t("hm_findingDrivers"), { city: ln(searchCity, "place") })} />
          ) : drivers.length === 0 ? (
            <EmptyStateBanner
              badge={t("hm_networkGrowing")}
              title={fillText(t("hm_noDriversIn"), { city: ln(searchCity, "place") })}
              description={t("hm_noDriversText")}
              primaryCta={t("hm_joinDriver")}
              onPrimaryAction={() => navigate("/alting-driver/onboarding")}
              secondaryCta={t("hm_changeCity")}
              onSecondaryAction={openCityPicker}
            />
          ) : shownDrivers.length === 0 ? (
            <ListMessage icon={<Icon name="search" size={22} strokeWidth={2} />}
              title={searchQuery.trim() ? fillText(t("hm_noDriversFound"), { q: searchQuery }) : t("hm_noMatchDrivers")}
              text={searchQuery.trim() ? t("hm_tryThese") : null}>
              {/* A car owner's name or a car searched on the wrong tab (mock Four_1 change 8). */}
              {searchQuery.trim() && (() => {
                const n = countNamed(carsListPath(searchCity), searchQuery, (c) => [c.driverName, c.ownerDriver?.name, c.carDetails?.model].join(" "));
                return n > 0 ? (
                  <button type="button" onClick={() => navigate("/", { state: { searchQuery } })}
                    className="w-full min-h-[40px] px-4 rounded-xl bg-[#7BAE00] text-white text-[11px] font-extrabold active:scale-[0.98]">
                    {fillText(t("sq_inCars"), { n })}
                  </button>
                ) : null;
              })()}
              {/* Languages are what this search reads best; each in its own script. */}
              {searchQuery.trim() && ["Telugu", "Hindi", "English"].map((s) => (
                <button key={s} type="button" onClick={() => { clearFilters(); setSearchQuery(s); }}
                  className="min-h-[40px] px-4 rounded-full bg-[#F1F7E3] text-[#3F5E00] text-[11px] font-bold active:scale-[0.97]">
                  {nativeLanguageNames(s, lang)}
                </button>
              ))}
              <button type="button" onClick={showAll} className={searchQuery.trim() ? SECONDARY_BTN : PRIMARY_BTN}>{t("hm_showAll")}</button>
            </ListMessage>
          ) : (
            <div className="space-y-2">
              {/* How many, and for what - said once for the whole list. */}
              <ResultStrip title={resultTitle} action={narrowed ? showAll : null} actionLabel={t("hm_showAll")}
                share={shareList} shareLabel={t("hm_shareList")} />

              {/* One driver a row on a phone (the owner's layout, Oct 2026); two where the
                  screen is wide. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {shownDrivers.map((d) => (
                  <DriverCard key={d.driverId || d.phone} d={d}
                    matchReason={matchReasons[d.driverId || d.phone] ? t(matchReasons[d.driverId || d.phone]) : undefined}
                    onHire={openHireOrPickRoute}
                    onCallToBook={handleCallToBook}
                    onSeePrice={openHireOrPickRoute}
                    callingId={callingId}
                    hasTripContext={hasTripContext}
                    fare={faresByDriver.get(d) || null}
                    onViewProfile={openHireOrPickRoute} />
                ))}
              </div>

              {/* Are you a driver? - below the listings */}
              {!isPartner && (
                <section className="rounded-2xl bg-white border border-[#E5E7EB] p-3 flex items-center gap-3 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
                  <span className="w-[34px] h-[34px] rounded-[10px] bg-[#F1F7E3] text-[#5E8700] flex items-center justify-center shrink-0">
                    <Icon name="user" size={18} strokeWidth={2.2} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11.5px] font-bold text-[#1F2937] leading-tight">{t("hm_joinDriverT")}</p>
                    <p className="text-[9.5px] text-[#64748B] leading-snug mt-0.5">{t("hm_joinDriverD")}</p>
                  </div>
                  <button type="button" onClick={() => navigate("/alting-driver/onboarding")}
                    className="shrink-0 min-h-[40px] px-4 rounded-[11px] border border-[#7BAE00] text-[#3F5E00] bg-white text-[11px] font-bold active:scale-[0.98]">
                    {t("hm_join")}
                  </button>
                </section>
              )}
            </div>
          )}
        </div>
        </div>
        </div>
      </div>
      <DesktopFooter t={t} />

      {/* Hire Modal */}
      {hireDriver && (
        <HireModal driver={hireDriver}
          carYear={carYear} carModel={carModel} fuelType={fuelType}
          fromLocation={fromLocation} toLocation={toLocation} referenceRates={referenceRates}
          onClose={() => setHireDriver(null)}
          onSuccess={() => { setHireDriver(null); setHireSuccess(true); }} />
      )}

      {/* Rating Modal */}
      {ratingBooking && (
        <HireRatingModal booking={ratingBooking}
          onClose={() => setRatingBooking(null)}
          onSuccess={handleRatingSuccess} />
      )}

      {/* The Filters sheet: the driver, the trip - and a button that counts. */}
      <FilterSheet
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        title={t("hm_filters")}
        groups={sheetGroups}
        values={driverFilters}
        countFor={(v) => applyDriverFilters(drivers, v).length}
        showLabel={(n) => (n === 0 ? t("hm_showNoDrivers") : n === 1 ? t("hm_showOneDriver") : fillText(t("hm_showDrivers"), { n }))}
        clearLabel={t("hm_clearAll")}
        closeLabel={t("hm_close")}
        onApply={(next) => {
          setDriverFilters({ ...NO_FILTERS, ...next });
          setShowFilterModal(false);
        }}
      />

      {tripsOpen && (
        <TripsSheet lang={lang} title={tripText(TRIP_TEXT.popular, lang)} sub={tripText(TRIP_TEXT.driverSub, lang)}
          trips={seasonTrips} priced={driverPriced} onOpen={pickSeasonTrip} onClose={() => setTripsOpen(false)} />
      )}
      {linkNote && (
        <div role="status" className="fixed left-1/2 -translate-x-1/2 bottom-[calc(84px+env(safe-area-inset-bottom,0px))] z-50 rounded-full bg-[#0F172A] text-white text-[11.5px] font-bold px-4 py-2 shadow-lg">
          {t("hm_linkCopied")}
        </div>
      )}
      <BottomNav />
    </>
  );
}
